import {useCallback, useState} from "react";
import {type Mode, nextUpgradePlan, planOf} from "home/gating";
import {useUser} from "../UserProvider";
import {postSendUpsell, shouldAskForFirstMonthOffer, type FirstMonthOffer, visibleOfferSpotsLeft} from "./firstMonthOffer";
import {useFirstMonthOfferState} from "./useFirstMonthOffer";
import type {PaidPlanId} from "pricing/planCatalogue";

/** What the post-send offer needs in order to render itself on the success card. */
export interface PostSendOffer {
	loading: boolean;
	/** The offer worth putting on screen, or null to render nothing at all. */
	offer: FirstMonthOffer | null;
	/** The cheapest tier this sender could buy — what the offer names, and the dialog preselects. */
	plan: PaidPlanId | null;
	/** The campaign's name key, e.g. `end-of-summer`, or null when it is unnamed. */
	occasion: string | null;
	/** The campaign's last open day, or null when it never closes — no deadline, so no countdown. */
	endsOn: string | null;
	/** The api's real available remainder, or null when it supplies none to show. */
	spotsLeft: number | null;
	/**
	 * Records that this sender has taken the offer, which withdraws it for the rest of this screen.
	 *
	 * Needed only since the instant path pays WITHOUT leaving the page (ui#1665): the plan it bought
	 * switches on when the `invoice.paid` webhook lands, so between the payment and that webhook
	 * `planOf(user)` still reads free and the row would go on offering a subscription the sender has
	 * already bought. The rule itself lives in `./firstMonthOffer`; this only holds the fact.
	 */
	markClaimed: () => void;
	/**
	 * This sender has taken the offer on this screen, which `offer` alone can no longer say.
	 *
	 * Taking it turns `offer` back to null, so a caller reading only that cannot tell "the campaign is
	 * shut today" from "the campaign was bought a second ago" — and the two want opposite treatment
	 * from whatever else competes for the placement. The success card's ladder coupon is the case that
	 * made this visible (api#1704): it admits itself only while no campaign is on screen, so without
	 * this it returned the moment the sender paid and offered the very plan they had just bought, for
	 * the whole `invoice.paid` webhook window during which `planOf(user)` still reads free.
	 */
	claimed: boolean;
}

/**
 * The discount offered once a transfer has gone out (kitecorp/transferify-ui#1455, widened by
 * ui#1556) — the fourth moment of kitecorp/transferify#117.
 *
 * Thin adapter, like every hook in this module: it reads the sender's plan and asks the api. Whether
 * any of that becomes an offer is decided by the React-free rules in `./firstMonthOffer`, and the
 * percentage itself always comes from the api.
 *
 * <b>The percentage and the DEADLINE, off one response.</b> The success card carries the full
 * treatment since ui#1653, so this reads the campaign fields it renders from the same single answer:
 * one live Stripe read either way, and asking for the number alone and then wanting the deadline would
 * have been two.
 *
 * <b>It carries the remaining-spots count again since ui#2949.</b> ui#1665 staged the two campaign
 * facts and moved the count into the claim dialog; the owner put it back on the card when the offer
 * moved onto the coupon's ticket (2026-09-27), which prints a remaining count where the coupon prints
 * its own. The dialog still does its own fresh per-tier reads through `./useFirstMonthPlans`.
 *
 * <b>The campaign travels with the OFFER.</b> Every campaign field is null whenever there is nothing
 * to show, so no caller can render a deadline or a scarcity count for a discount this sender is not
 * being made.
 *
 * <b>No dismissal, unlike the post-signup moment.</b> This surface exists only for the length of one
 * success screen, so there is nothing durable to remember; `./offerDismissal`'s own header records
 * why the two moments never suppress each other.
 *
 * @param mode           how the transfer that just finished was sent — WHEN each mode qualifies is
 *                       decided by the rule in `./firstMonthOffer`, never by this adapter
 * @param servedDownload a P2P recipient has received a whole file (ui#1665); always false for a hosted
 *                       send, where the upload itself is the completed transfer
 * @param automatic      what the caller already knows about discounts shown to this account WITHOUT
 *                       being asked for. `seenToday` is the only member and is read once per success
 *                       screen: a coupon presented on THIS screen is deliberately not one of these,
 *                       because it competes with this offer for the same slot (see `./firstMonthOffer`)
 */
export function usePostSendOffer(mode: Mode, servedDownload: boolean,
	automatic: {seenToday?: boolean} = {}): PostSendOffer {
	const [user] = useUser();
	const plan = planOf(user);
	const target = nextUpgradePlan(plan);
	// Latched rather than derived: what it records is a payment that has already happened, so no later
	// read of the account may un-record it. Reset by nothing — a sender who wants another plan goes to
	// the billing surfaces, not back through a first-month coupon they have already spent.
	const [claimed, setClaimed] = useState(false);
	const markClaimed = useCallback(() => setClaimed(true), []);
	// Asked only for a sender who could act on the answer. The api's live Stripe coupon read is bounded
	// by offers shown, so this gate is a cost decision as well as a product one.
	const {offer: quoted, loading} = useFirstMonthOfferState(
		target, shouldAskForFirstMonthOffer({mode, plan, servedDownload, claimed, ...automatic}));
	const offer = postSendUpsell(
		{mode, plan, servedDownload, claimed, quotedPercentOff: quoted?.percentOff ?? null, ...automatic});
	return {
		loading,
		offer,
		plan: target,
		occasion: offer === null ? null : quoted?.occasion ?? null,
		endsOn: offer === null ? null : quoted?.endsOn ?? null,
		spotsLeft: offer === null ? null : visibleOfferSpotsLeft(quoted?.spotsLeft),
		markClaimed,
		claimed,
	};
}
